// The direct-request surfaces (bead hf-e6-commitments-coverage-udt.1, plan §10.4).
//
// Pure components over `requestData.ts`. Copy from `@herefor/product-language`,
// components from `@herefor/design-system`, and no decision about permission here.
//
// ── THE THREE WORDS, AGAIN, AND THIS IS WHERE THEY MEET ────────────────────────
//
//   COULD HELP  — a possibility somebody recorded about their own week.
//   ASKED       — a question an organiser chose to send to one named person.
//   AGREED      — a promise.
//
// `MatchingSurfaces.tsx` keeps the first two apart. THIS file is where the second
// becomes the third, or does not — and it is therefore the one place a careless
// sentence could make a question look like an answer.
//
// So, concretely, and each of these is asserted in the tests beside this file:
//
//   - `AskAudience` never shows a tick, a count, or a "covered" tone. It reports who
//     was asked and how each question ended. The need's own chip is the only thing on
//     the screen entitled to say whether anybody is covered.
//   - `LAPSED IS LOUD`. An ask that expired gets a `warning` tone and a sentence
//     that offers the next move, because an ask that quietly died is how a need gets
//     stranded behind a question nobody remembers sending (REQ-009).
//   - `PendingAskCard` shows the consequence of Yes BEFORE the buttons, in the same
//     words the volunteer flow uses — accepting an ask and volunteering create the
//     same commitment, and a person must not learn different things about one promise
//     from two screens.
//   - The two answers are the SAME SIZE. A prominent Yes beside a quiet "no thanks"
//     link would tell somebody which answer the product wants, and this product does
//     not have a preference about whether a family member can drive on Tuesday.

import { Button, Card, EmptyState, StatusMessage } from "@herefor/design-system";
import {
  DIRECT_REQUEST_EXPIRY_POLICY_MESSAGES as EXPIRY_COPY,
  DIRECT_REQUEST_MESSAGES as COPY,
  MATCHING_SURFACE_MESSAGES as MATCHING_COPY,
} from "@herefor/product-language";
import type { requests as contracts } from "@herefor/contracts";
import { useEffect, useState } from "react";

import type { AskView } from "./requestData";

/** What the caller is doing right now, so a card can disable exactly itself. */
export type AskState =
  | { readonly status: "idle" }
  | { readonly status: "sending"; readonly directRequestId: string }
  | {
      readonly status: "refused";
      readonly directRequestId: string;
      readonly message: string;
    };

/**
 * A monotonic browser clock. Tests inject one so crossing a deadline never waits for
 * real time; production uses `performance.now()` and a single cancellable timeout.
 */
export interface DeadlineClock {
  readonly monotonicNow: () => number;
  readonly schedule: (callback: () => void, delayMs: number) => number;
  readonly cancel: (handle: number) => void;
}

const browserDeadlineClock: DeadlineClock = {
  monotonicNow: () => performance.now(),
  schedule: (callback, delayMs) => window.setTimeout(callback, delayMs),
  cancel: (handle) => {
    window.clearTimeout(handle);
  },
};

/**
 * Hide helper actions once the server-provided deadline has passed.
 *
 * This can only remove a row that the server said was open. It cannot add one, so
 * the browser view is a conservative subset of the authoritative query result.
 */
export function projectPendingAsksAt(asks: readonly AskView[], now: number): readonly AskView[] {
  return asks.filter((ask) => ask.expiresAt > now);
}

/**
 * Move newly elapsed organiser rows from awaiting to lapsed for presentation only.
 * Every row the server already ended stays in its original ended group.
 */
export function projectAskAudienceAt(
  audience: contracts.NeedRequestsView,
  now: number,
): contracts.NeedRequestsView {
  const stillAwaiting = audience.awaiting.filter((participant) => participant.expiresAt > now);
  if (stillAwaiting.length === audience.awaiting.length) return audience;

  const newlyLapsed = audience.awaiting.filter((participant) => participant.expiresAt <= now);
  return {
    ...audience,
    awaiting: stillAwaiting,
    lapsed: [...audience.lapsed, ...newlyLapsed],
  };
}

function useServerDeadlineNow(
  evaluatedAt: number,
  deadlines: readonly number[],
  clock: DeadlineClock,
): number {
  const [projectedNow, setProjectedNow] = useState(evaluatedAt);
  // A newer server snapshot may advance this immediately. It never moves backward:
  // doing so could briefly make an ask appear open again between render and effect.
  const now = Math.max(projectedNow, evaluatedAt);
  const nextDeadline = deadlines.reduce<number | null>(
    (next, deadline) => (deadline > now && (next === null || deadline < next) ? deadline : next),
    null,
  );

  useEffect(() => {
    if (nextDeadline === null) return;
    const monotonicAt = clock.monotonicNow();
    const handle = clock.schedule(
      () => {
        setProjectedNow(now + Math.max(0, clock.monotonicNow() - monotonicAt));
      },
      Math.max(0, Math.ceil(nextDeadline - now)),
    );
    return () => {
      clock.cancel(handle);
    };
  }, [clock, nextDeadline, now]);

  return now;
}

/* ============================================================ the helper side === */

export interface PendingAskListProps {
  readonly asks: readonly AskView[];
  readonly state: AskState;
  readonly onAnswer: (ask: AskView, response: "accept" | "decline") => void;
  /**
   * Disable both answers because the app cannot write right now (PLT-011).
   *
   * A seam rather than a store read, exactly as `RecheckList` has: this file is a pure
   * component and one that imported a connectivity global would stop being testable
   * without one. BOTH answers are disabled together — disabling only Yes would leave
   * No as the one thing a person could still do, a nudge toward declining produced by
   * an implementation detail rather than by anybody's intent.
   */
  readonly actionsPaused?: boolean;
  /**
   * Render nothing when there is nothing to answer.
   *
   * For placements that are somebody else's screen — Today, whose job is the day. The
   * calm empty state belongs on the screen that is ABOUT asks; everywhere else,
   * "nobody has asked you to do anything" is a sentence nobody needed to read.
   */
  readonly hideWhenEmpty?: boolean;
  /** Injectable monotonic clock for deterministic deadline rendering tests. */
  readonly deadlineClock?: DeadlineClock;
}

/**
 * The asks waiting for this person, answered one at a time.
 *
 * One card per question, never collapsed into "accept all". Agreeing to three
 * different pieces of somebody's week with one tap is bulk consent, and the fact that
 * it would be more convenient is exactly why it is not offered (AVL-026, §10.9's rule
 * applied to the other half of the loop).
 */
export function PendingAskList({
  asks,
  state,
  onAnswer,
  actionsPaused = false,
  hideWhenEmpty = false,
  deadlineClock = browserDeadlineClock,
}: PendingAskListProps) {
  const evaluatedAt = asks.reduce(
    (latest, ask) => Math.max(latest, ask.evaluatedAt),
    asks[0]?.evaluatedAt ?? 0,
  );
  const deadlineNow = useServerDeadlineNow(
    evaluatedAt,
    asks.map((ask) => ask.expiresAt),
    deadlineClock,
  );
  const visibleAsks = projectPendingAsksAt(asks, deadlineNow);

  // THE ANSWER THAT OUTLIVES ITS QUESTION (REQ-007).
  //
  // A late yes is refused AND closes the ask, so the query re-runs and the card
  // carrying the explanation unmounts in the same tick it was written. Without this,
  // somebody who said yes a second too late would watch the question vanish and be
  // told nothing at all — the exact silence `alreadyCovered` exists to prevent.
  //
  // So a refusal whose ask is no longer in the list is rendered HERE, at list level,
  // where nothing can unmount it. A refusal whose ask is still standing stays on its
  // own card, beside the buttons the person can press again.
  const outcome =
    state.status === "refused" &&
    !visibleAsks.some((ask) => ask.directRequestId === state.directRequestId)
      ? state.message
      : null;

  if (hideWhenEmpty && visibleAsks.length === 0 && outcome === null) return null;

  return (
    <section aria-labelledby="pending-ask-heading" className="asks">
      {/* THE HELPER'S HEADING, NOT THE ORGANISER'S (fixed in bead
          hf-e6-commitments-coverage-udt.9, when this list moved onto the helper's own
          screen and the mistake became legible). "Who has been asked" is what an
          organiser reads on a need — a roll-call of other people. Over somebody's own
          questions it told them they were looking at somebody else's business. */}
      <h2 id="pending-ask-heading">{COPY.yourAsksHeading}</h2>

      {/* `info`, not `warning`. Being thanked for arriving second is not a problem
          somebody has to solve, and a warning tone would make a kind act feel like an
          error. `data-ask-outcome` gives the E2E something stable to read, the way
          `data-claim-outcome` does for the volunteer flow. */}
      {outcome === null ? null : (
        <StatusMessage tone="info">
          <span data-ask-outcome="closed">{outcome}</span>
        </StatusMessage>
      )}

      {visibleAsks.length === 0 ? (
        // Calm, and not an invitation to go looking for work. "Nobody has asked you to
        // do anything" is a complete, restful sentence. Suppressed while an outcome is
        // standing: "nobody has asked you" directly under "someone else said yes"
        // would read as a contradiction.
        outcome === null ? (
          <EmptyState description={COPY.noneForYou} title={COPY.noneForYouTitle} />
        ) : null
      ) : (
        <ul className="asks__list">
          {visibleAsks.map((ask) => (
            <li key={ask.directRequestId}>
              <PendingAskCard
                actionsPaused={actionsPaused}
                ask={ask}
                onAnswer={onAnswer}
                state={state}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface PendingAskCardProps {
  readonly ask: AskView;
  readonly state: AskState;
  readonly onAnswer: (ask: AskView, response: "accept" | "decline") => void;
  /** See `PendingAskListProps.actionsPaused`. */
  readonly actionsPaused?: boolean;
}

export function PendingAskCard({
  ask,
  state,
  onAnswer,
  actionsPaused = false,
}: PendingAskCardProps) {
  const busy =
    actionsPaused || (state.status === "sending" && state.directRequestId === ask.directRequestId);
  const refusal =
    state.status === "refused" && state.directRequestId === ask.directRequestId
      ? state.message
      : null;

  return (
    <Card title={ask.label} titleLevel={3}>
      {/* The ask is from a PERSON. "You have been asked" with nobody attached would
          make a family member's question read as an automated demand. */}
      <p className="asks__from">{COPY.askHeading(ask.requestedByName)}</p>

      <p className="asks__when">
        {ask.dateLabel} · {ask.windowLabel}
      </p>

      {ask.href === null ? null : (
        <p className="asks__link">
          <a href={ask.href}>{ask.label}</a>
        </p>
      )}

      {/* The consequence, BEFORE the buttons (COM-001). Identical in substance to the
          volunteer flow's, because it is the same promise. */}
      <p className="asks__meaning">{COPY.acceptMeaning}</p>

      {/* A fact about the question, not a countdown. No urgency styling, no "hurry" —
          the ask has a horizon and the person is told what it is (REQ-009). */}
      <p className="asks__expires">{COPY.expiresOn(ask.expiresOnLabel)}</p>

      {refusal === null ? null : <StatusMessage tone="warning">{refusal}</StatusMessage>}

      <div className="asks__answers">
        {/* Same size, same weight, in the order Yes-then-No only because that is the
            order the question is asked in. Neither is styled as the expected answer. */}
        <Button
          disabled={busy}
          onClick={() => {
            onAnswer(ask, "accept");
          }}
          variant="primary"
        >
          {COPY.accept}
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            onAnswer(ask, "decline");
          }}
          variant="secondary"
        >
          {COPY.decline}
        </Button>
      </div>
    </Card>
  );
}

/* ========================================================= the organiser side === */

export interface AskAudienceProps {
  readonly audience: contracts.NeedRequestsView;
  /** Withdrawing an ask. Absent when the reader may not send asks (AUT-007). */
  readonly onWithdraw?: (participant: contracts.RequestParticipant) => void;
  readonly actionsPaused?: boolean;
  /** Injectable monotonic clock for deterministic deadline rendering tests. */
  readonly deadlineClock?: DeadlineClock;
}

/**
 * The state of every question this organiser sent, beside the need they sent it about.
 *
 * THE HALF OF EXPIRY THAT IS NOT SILENT. `lapsed` is why this component exists: an ask
 * that simply vanished would leave the person who sent it believing somebody was still
 * considering it (REQ-009).
 *
 * NOTHING HERE IS A COVERAGE STATEMENT. No counts, no ticks, no "1 of 2". The lists say
 * who was asked and how each question ended; whether the need is covered is the need's
 * own chip, derived from commitments, and duplicating it here would be a second answer
 * to the one question this product must never get wrong.
 */
export function AskAudience({
  audience,
  onWithdraw,
  actionsPaused = false,
  deadlineClock = browserDeadlineClock,
}: AskAudienceProps) {
  const deadlineNow = useServerDeadlineNow(
    audience.evaluatedAt,
    audience.awaiting.map((participant) => participant.expiresAt),
    deadlineClock,
  );
  const visibleAudience = projectAskAudienceAt(audience, deadlineNow);
  const total =
    visibleAudience.awaiting.length +
    visibleAudience.lapsed.length +
    visibleAudience.declined.length +
    visibleAudience.accepted.length;

  // Renders nothing when nobody has ever been asked. A permanent "nobody has been
  // asked about this yet" panel on every freshly created need would be noise on the
  // screen an organiser uses most — the sentence is for the case where somebody DID
  // ask and wants to know what happened.
  if (total === 0) return null;

  return (
    <section aria-labelledby="ask-audience-heading" className="asks-audience">
      <h2 id="ask-audience-heading">{COPY.audienceHeading}</h2>

      {visibleAudience.awaiting.length === 0 ? null : (
        <AudienceGroup
          heading={COPY.awaitingHeading}
          modifier="awaiting"
          participants={visibleAudience.awaiting}
          {...(onWithdraw === undefined ? {} : { onWithdraw })}
          actionsPaused={actionsPaused}
        />
      )}

      {/* LOUD, on purpose, and the only group with a `warning` tone. Each lapse gets
          its own sentence naming the person and offering the next move, rather than a
          list under a heading — a name in a list is read as information, and this is
          something the organiser has to do something about. */}
      {visibleAudience.lapsed.map((participant) => (
        <StatusMessage key={participant.directRequestId} tone="warning">
          {COPY.lapsed(participant.displayName)}
        </StatusMessage>
      ))}

      {visibleAudience.declined.length === 0 ? null : (
        <AudienceGroup
          heading={COPY.declinedHeading}
          modifier="declined"
          participants={visibleAudience.declined}
        />
      )}

      {visibleAudience.accepted.length === 0 ? null : (
        <AudienceGroup
          heading={COPY.acceptedHeading}
          modifier="accepted"
          participants={visibleAudience.accepted}
        />
      )}
    </section>
  );
}

function AudienceGroup({
  heading,
  modifier,
  participants,
  onWithdraw,
  actionsPaused = false,
}: {
  readonly heading: string;
  readonly modifier: string;
  readonly participants: readonly contracts.RequestParticipant[];
  readonly onWithdraw?: (participant: contracts.RequestParticipant) => void;
  readonly actionsPaused?: boolean;
}) {
  return (
    <div className={`asks-audience__group asks-audience__group--${modifier}`}>
      <p className="asks-audience__heading">{heading}</p>
      <ul className="asks-audience__names">
        {participants.map((participant) => (
          <li key={participant.directRequestId}>
            <span>{participant.displayName}</span>
            {onWithdraw === undefined ? null : (
              <Button
                disabled={actionsPaused}
                onClick={() => {
                  onWithdraw(participant);
                }}
                variant="secondary"
              >
                {COPY.withdrawAsk(participant.displayName)}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================== the send step === */

export interface AskConfirmationProps {
  readonly displayName: string;
  /** Effective circle policy, read by the server beside the ask audience (REQ-009). */
  readonly expiryPolicy: contracts.RequestExpiryPolicyView;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly busy?: boolean;
  /** A refusal from the server, already turned into a sentence. */
  readonly problem?: string | null;
}

/**
 * The step between "Ask Mary" and an ask being sent.
 *
 * NOT ceremony. It exists because the sentence in it is the organiser-facing half of
 * the promise the availability screen made to the helper — "this does not book them
 * in, and this still needs someone until they say yes" — and there is nowhere else in
 * the product where an organiser is told that at the moment it matters.
 *
 * It is also what stops "Ask" being a one-tap accident in a list of names, which is
 * the interaction that would turn a could-help list into a roster.
 */
export function AskConfirmation({
  displayName,
  expiryPolicy,
  onConfirm,
  onCancel,
  busy = false,
  problem = null,
}: AskConfirmationProps) {
  return (
    <Card title={COPY.confirmHeading(displayName)} titleLevel={2}>
      <p className="asks__meaning">{COPY.confirmMeaning(displayName)}</p>
      <p className="asks__expires" data-request-expiry-policy>
        {EXPIRY_COPY.atAsk(expiryPolicy.unansweredTtlDays)}
      </p>

      {problem === null ? null : <StatusMessage tone="warning">{problem}</StatusMessage>}

      <div className="asks__answers">
        <Button disabled={busy} onClick={onConfirm} variant="primary">
          {COPY.confirmSend}
        </Button>
        <Button disabled={busy} onClick={onCancel} variant="secondary">
          {COPY.confirmCancel}
        </Button>
      </div>
    </Card>
  );
}

/* ======================================================== asking anyone else === */

export interface AskablePersonView {
  readonly personId: string;
  readonly displayName: string;
  /** Shown beside the name when the circle recorded one; never invented here. */
  readonly relationshipLabel: string | null;
}

export interface AskAnyoneListProps {
  readonly people: readonly AskablePersonView[];
  /** Server-decided: may this person send an ask? Never inferred here (AUT-007). */
  readonly canAsk: boolean;
  readonly onAsk?: (person: AskablePersonView) => void;
}

/**
 * The circle, as names an organiser may ask (REQ-001; bead hf-mtk).
 *
 * ── WHY THIS EXISTS BESIDE THE COULD-HELP LIST, RATHER THAN INSTEAD OF IT ──────
 *
 * The could-help list holds only the people whose recorded availability CONTAINS the
 * need's window (AVL-021). That is exactly right for what it answers — "who said they
 * were free" — and exactly wrong as the only way to ask, because the members least
 * likely to record availability are the ones an organiser most often needs to ask
 * directly (§7.2). The two lists answer different questions and both are true.
 *
 * A name may appear in both. That is deliberate: this list is the CIRCLE, and a
 * complete list you can scan is worth more than a clever one where somebody is
 * missing for a reason you have to reconstruct. Nobody is hidden from it except the
 * people already holding a question about this need, who are in the audience panel
 * above with a control to take that question back.
 *
 * ── NO BULK, BY CONSTRUCTION ──────────────────────────────────────────────────
 *
 * There is no list-level action, no checkbox, and no selection state anywhere in this
 * component — at any list size. A "ask everyone who could help" button would have to
 * be written from scratch, in a file whose tests assert it does not exist (AVL-026,
 * REQ-010). Each row hands ONE name to the caller, which opens the same
 * `AskConfirmation` the could-help list opens, with the same words.
 */
export function AskAnyoneList({ people, canAsk, onAsk }: AskAnyoneListProps) {
  return (
    <Card title={COPY.anyoneHeading} titleLevel={2}>
      <p className="asks__meaning">{COPY.anyoneMeaning}</p>

      {people.length === 0 ? (
        <EmptyState description={COPY.anyoneNoneBody} title={COPY.anyoneNoneTitle} />
      ) : (
        <ul className="asks__people">
          {people.map((person) => (
            <li className="asks__person" key={person.personId}>
              <p className="asks__person-name">{person.displayName}</p>
              {person.relationshipLabel === null ? null : (
                <p className="asks__person-relationship">{person.relationshipLabel}</p>
              )}
              {canAsk && onAsk !== undefined ? (
                <Button
                  onClick={() => {
                    onAsk(person);
                  }}
                  variant="secondary"
                >
                  {/* The could-help list's own label, so one person reads the same
                      four words wherever the circle offers to ask on their behalf. */}
                  {MATCHING_COPY.ask(person.displayName)}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
